import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import bus from 'lib/bus';
import { getValueForTag, decodeObjectID } from 'utils/general';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import {
  trackProductFindShade,
  trackTryOn,
  trackTryShadeOn,
  trackFindMyShade,
} from 'lib/tracking';
import { Pagination, A11y, Keyboard } from 'swiper/modules';

/**
 * Product Gallery component
 */
export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    'transition-expand': () =>
      import('../transition-expand/transition-expand.vue'),
    'vue-swiper': () => import('../vue-swiper/vue-swiper.vue'),
    'product-gallery-mobile-slide': () =>
      import('../product-gallery-mobile/product-gallery-mobile-slide.vue'),
    modal: () => import('../modal/modal.vue'),
    'product-shade-action-buttons': () => import('../product-shade-action-buttons/product-shade-action-buttons.vue')
  },
  props: [
    'collectionAwards'
  ],
  data() {
    return {
      swiper: null,
      thumbScrollIndex: 0,
      showingZoomGallery: false,
      isTryCombination: false,
      isLoading: true,
      parentProductId: null,
      hasVideo: false,
      initialIndex: 0,
      galleryLoading: false,
      swiperOptions: {
        modules: [A11y, Keyboard],
        slidesPerView: 1.3,
        spaceBetween: 0,
        a11y: true,
        keyboard: true,
        on: {
          slideChange: (swiper) => {
            this.setIndex(swiper.activeIndex);
          }
        },
      },
      tagText: 'Shimmer',
      tagText2: 'Matte',
      productTitle: '',
      secondaryProductTitle: '',
      campaignType: 'solo',
      showingCombo: false,
      shades: window.__GLOBAL__.shades.values,
      circleBackgroundColor1: '#fff',
      circleBackgroundColor2: '#fff',
      patternObject: {
        0: { color1: '7462199754797', color2: '7459287957549' },
        1: { color1: '7462199820333', color2: '7459321446445' },
        2: { color1: '7462200016941', color2: '7459327901741' },
        3: { color1: '7462200148013', color2: '7459359457325' },
        4: { color1: '7462200279085', color2: '7459346579501' },
        5: { color1: '7462200344621', color2: '7462193954861' },
        6: { color1: '7462200410157', color2: '7462193201197' },
        7: { color1: '7462200573997', color2: '7462199689261' },
      },
    };
  },
  created() {
    bus.$on('update-variant', this.handleVariantChange);
  },
  watch: {
    async product() {
      if (this.showingCombo) {
        this.handleCombinationChange(this.product.id);
      } else {
        await this.getParentProductId();
        if (window.YMK) window.YMK.applyMakeupBySku(this.parentProductId);
        const colorIdTag = this.product.tags?.find((t) =>
          t.includes('Color ID'),
        );
        const colorId = colorIdTag?.split('|')[1];
        const shade = this.shades.find((shade) => shade.id === colorId);
        this.tagText =
          this.product?.metafields?.swatch_management?.additional_categories;
        this.productTitle = this.product?.variants[0]?.title;
        this.circleBackgroundColor1 = shade?.hex;
      }

      this.galleryLoading = true;
      this.scrollToActiveImage();
    },
  },
  computed: {
    ...mapState('product', ['loading', 'product', 'metafields', 'activeImage', 'showingTryShade']),
    ...mapGetters('product', ['productTitle']),
    currentVideoUrl() {
      if (!this.product?.media.length) return false;
      let currentMedia = this.product.media[this.activeImage];
      if (!Boolean(currentMedia)) {
        currentMedia = this.product.media[0];
        this.setIndex(0);
      }

      if (currentMedia.type !== 'video') return false;

      const url =
        currentMedia.host === 'YOUTUBE'
          ? `https://www.youtube.com/embed/${currentMedia.url
              .split('/')
              .pop()}?enablejsapi=1`
          : `https://player.vimeo.com/video/${currentMedia.url.split('/')})`;
      return url;
    },
    productAwards() {
      return this.collectionAwards && this.collectionAwards.length ? this.collectionAwards : this.product.awards ? this.product.awards : [];
    }
  },
  mounted() {
    this.isLoading = false;
  },
  methods: {
    ...mapActions('product', ['updateActiveImage']),
    ...mapActions('global', ['openModal', 'setCurrentProductGalleryVideo']),
    ...mapMutations('product', ['setShowingTryShade']),
    handleGalleryTracking() {
      dataLayer.push({
        event: 'product_module_interaction',
        click_action: 'click',
        product_name: this.productTitle,
        module_name: 'product gallery',
        shopify_module_name: 'product gallery mobile'
      });
    },
    bindTracking() {
      window.YMK.addEventListener('reset', () => {
        trackTryShadeOn({ action: 'try_shade_on_reset', shopify_module_name: 'product gallery mobile' });
      });

      window.YMK.addEventListener('compare', () => {
        trackTryShadeOn({ action: 'try_shade_on_compare', shopify_module_name: 'product gallery mobile' });
      });

      window.YMK.addEventListener('compareDisabled', () => {
        trackTryShadeOn({ action: 'try_shade_on_compare_disable', shopify_module_name: 'product gallery mobile' });
      });

      window.YMK.addEventListener('photoSaved', () => {
        trackTryShadeOn({ action: 'try_shade_on_save_photo', shopify_module_name: 'product gallery mobile' });
      });
    },
    handleVariantChange(variant) {
      const variantImageIndex = this.product.media.findIndex(
        (image) => image.src === variant.image.src,
      );
      if (variantImageIndex > -1) {
        this.setIndex(variantImageIndex);
        this.scrollToActiveImage();
      }
    },
    setIndex(index) {
      this.updateActiveImage(index);
      this.handleGalleryTracking();
    },
    getVimeoUrl(media) {
      return `${media.url}?background=0&autoplay=0&loop=1&muted=1`;
    },
    scrollToActiveImage() {
      const previousIndex = this.activeImage;
      if (this.$refs.carousel) {
        this.$refs.carousel.swiper().slideTo(previousIndex);
      }
      this.galleryLoading = false;
    },
    async getParentProductId() {
      if (!Object.hasOwn(this.product, 'title')) return;
      // If the product is a mini, try to query for the parent/full size product
      if (this.product.title.toLowerCase().includes('mini')) {
        const productHandle = this.product.handle.replace('-mini', '');
        const response = await storefront.request(queries.product, {
          handle: productHandle,
        });
        if (response?.product?.id) {
          this.parentProductId = decodeObjectID(response.product.id);
        } else {
          this.parentProductId = this.product.id;
        }
      } else {
        this.parentProductId = this.product.id;
      }
    },
    toggleZoomGallery(media = false) {
      if (media && media.type === 'video') {
        if (media.host === 'VIMEO') return;

        this.setCurrentProductGalleryVideo(media);
        this.openModal('videoModal');
      } else {
        if (this.showingZoomGallery) {
          enableBodyScroll();
        } else {
          disableBodyScroll();
        }
        this.showingZoomGallery = !this.showingZoomGallery;
      }
    },
    showIndex(index) {
      this.initialIndex = index;
    },
    toggleSoloCombination(type) {
      this.campaignType = type;
      if (this.isShadowStix) {
        if (type === 'solo') {
          if (window.YMK.isLoaded()) {
            window.YMK.reset();
            window.YMK.applyMakeupBySku(this.product.id);
          } else {
            window.YMK.applyMakeupBySku(this.product.id);
            window.YMK.open({ loadLiveCam: true });
          }

          trackTryOn(this.product);
          trackTryShadeOn({ action: 'try_shade_on_open', shopify_module_name: 'product gallery mobile' });
          this.showingCombo = false;
          this.isTryCombination = false;
          this.setLeftShadeButton(this.product.id);
        } else {
          this.setTryCombination();
        }
        this.setShowingTryShade(true);
      }
    },
    async setTryCombination() {
      const { mainColorId, secondaryColorId } = await this.getComboProductsIds(
        this.product.id,
      );

      this.loadMakeupByPattern(mainColorId, secondaryColorId, true);
    },
    async handleCombinationChange(id) {
      if (this.isShadowStix && this.campaignType === 'combo') {
        const { mainColorId, secondaryColorId } =
          await this.getComboProductsIds(id);
        this.loadMakeupByPattern(mainColorId, secondaryColorId, false);
      }
    },
    loadMakeupByPattern(mainColorId, secondaryColorId, isInitialLoad) {
      setTimeout(() => {
        window.YMK.applyMakeupByPattern('2 colors99', [
          { itemGuid: mainColorId, colorIndex: 0, colorIntensity: 78 },
          {
            itemGuid: secondaryColorId,
            colorIndex: 0,
            colorIntensity: 78,
          },
        ]);
        trackTryOn(this.product);
        trackTryShadeOn({ action: 'try_shade_on_open', shopify_module_name: 'product gallery mobile' });
      }, 2000);

      this.showingCombo = true;
      if (isInitialLoad) {
        this.setShowingTryShade(true);
        this.isTryCombination = true;
      }
    },
    async isShimmer(id) {
      const { product } = await this.getShadowstixProductById(id);
      if (!product) return;
      return product?.shadeDetails?.value.toLowerCase() === 'shimmer';
    },
    async setLeftShadeButton(id) {
      const { product } = await this.getShadowstixProductById(id);

      if (product) {
        const colorIdTag = product.tags?.find((t) => t.includes('Color ID'));
        if (colorIdTag) {
          const colorId = colorIdTag?.split('|')[1];
          const shade = this.shades.find((shade) => shade.id === colorId);
          this.circleBackgroundColor1 = shade?.hex;
        }
        this.tagText = product?.shadeDetails?.value;
        this.productTitle = product?.variants.edges[0]?.node.title;
      }
    },
    async setRightShadeButton(id) {
      const { product } = await this.getShadowstixProductById(id);

      if (product) {
        const colorIdTag = product.tags?.find((t) => t.includes('Color ID'));
        if (colorIdTag) {
          const colorId = colorIdTag.split('|')[1];
          const shade = this.shades.find((shade) => shade.id === colorId);
          this.circleBackgroundColor2 = shade?.hex;
        }
        this.tagText2 = product?.shadeDetails?.value;
        this.secondaryProductTitle = product?.variants.edges[0]?.node.title;
      }
    },
    async setProductData(mainColorId, secondaryColorId) {
      const mainIsShimmer = await this.isShimmer(mainColorId);

      if (mainIsShimmer) {
        this.setLeftShadeButton(mainColorId);
        this.setRightShadeButton(secondaryColorId);
      } else {
        this.setLeftShadeButton(secondaryColorId);
        this.setRightShadeButton(mainColorId);
      }
    },
    async getComboProductsIds(id) {
      let mainColorId;
      let secondaryColorId;

      for (const key in this.patternObject) {
        if (
          this.patternObject[key].color1 === id ||
          this.patternObject[key].color2 === id
        ) {
          if (this.patternObject[key].color1 === id) {
            mainColorId = this.patternObject[key].color1;
            secondaryColorId = this.patternObject[key].color2;

            this.setProductData(mainColorId, secondaryColorId);
          } else {
            mainColorId = this.patternObject[key].color2;
            secondaryColorId = this.patternObject[key].color1;

            this.setProductData(mainColorId, secondaryColorId);
          }
          break;
        }
      }

      const mainIsShimmer = await this.isShimmer(mainColorId);
      return mainIsShimmer
        ? { mainColorId: secondaryColorId, secondaryColorId: mainColorId }
        : { mainColorId, secondaryColorId };
    },
    getProductSKU() {
      let sku = getValueForTag('Makeup SKU', this.product.tags);
      if (!sku) {
        sku = this.product.variants[0].sku;
      }
      return sku;
    },
    async getShadowstixProductById(id) {
      const response = await storefront.request(
        queries.getShadowstixProductById,
        {
          id: 'gid://shopify/Product/' + id,
        },
      );
      return response;
    },
  },
};
